import { useState } from "react";
import { keyframes, styled } from "styled-components";

import {
  Colored,
  colors,
  ControlButton,
  easeOutCirc,
  FocusAnimation,
  levels,
  Text,
} from "@vericus/cadmus-ui";

import * as styles from "./clock.css";
import ClockSvgIcon from "./ClockSvgIcon";
import CloseSvgIcon from "./CloseSvgIcon";

interface Props {
  /** Main message to render when the clock is open */
  message: string;
  /** Message suffix rendered beside the main message */
  suffix: string;
  /** Render an inline version of the clock that is not floating on the page. */
  inline?: boolean;
}

/**
 * PureClock is a React component for rendering the floating clock with
 * a message displayed when the clock is open.
 */
export function PureClock(props: Props) {
  const { inline = false } = props;

  if (inline) {
    return <PureClockInline {...props} />;
  }

  return <PureClockFloating {...props} />;
}

/// NEW Inline PureClock

function PureClockInline(props: Props) {
  const { message, suffix } = props;
  const [open, setOpen] = useState(true);

  if (!open) {
    return <ControlButton icon={<ClockIcon />} onClick={() => setOpen(true)} />;
  }

  return (
    <button
      className={styles.root}
      tabIndex={0}
      onClick={() => setOpen(false)}
      role="timer"
      aria-live="polite"
      aria-atomic="true"
    >
      <ClockIcon />
      <Text
        kind="systemMd"
        aria-label="Time remaining"
        as="div"
        color="navy800"
      >
        {message} <Colored color="navy400">{suffix}</Colored>
      </Text>
    </button>
  );
}

/// Floating PureClock

function PureClockFloating(props: Props) {
  const { message, suffix } = props;
  const [open, setOpen] = useState(true);
  return (
    <Container>
      {open && (
        <Detail role="timer" aria-live="polite" aria-atomic="true">
          <Text kind="systemMd" aria-label="Time remaining">
            {message} <Colored color="grey500">{suffix}</Colored>
          </Text>
        </Detail>
      )}
      <IconButton
        title="Time progress indicator"
        aria-label="Time progress indicator"
        aria-haspopup
        aria-expanded={open}
        tabIndex={0}
        onClick={() => setOpen(!open)}
      >
        {open ? <CloseSvgIcon /> : <ClockSvgIcon />}
      </IconButton>
    </Container>
  );
}

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 1.26562C11.7195 1.26562 14.7343 4.28055 14.7344 8C14.7344 11.7195 11.7195 14.7354 8 14.7354C4.28051 14.7353 1.26562 11.7195 1.26562 8C1.26569 4.28056 4.28055 1.26564 8 1.26562Z"
      fill="#3F5982"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.61928 4.39014C7.93487 4.39014 8.19071 4.64597 8.19071 4.96157V8.46527L10.222 9.81944C10.4846 9.9945 10.5555 10.3493 10.3805 10.6119C10.2054 10.8745 9.85061 10.9454 9.58802 10.7704L7.30231 9.24655C7.14334 9.14057 7.04785 8.96215 7.04785 8.77109V4.96157C7.04785 4.64597 7.30369 4.39014 7.61928 4.39014Z"
      fill="#3F5982"
    />
  </svg>
);

const Container = styled.div`
  display: inline-block;
  position: relative;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  box-sizing: boder-box;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: none;
  background: ${colors.black200};
  fill: ${colors.black200};
  color: ${colors.white100};
  box-shadow: ${levels.three};

  cursor: pointer;

  &:not(:disabled) {
    /* The :before pseudo-element creates a focus and ping outline*/
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      border-radius: 50%;
      width: 36px;
      height: 36px;
    }

    &:focus:before {
      ${FocusAnimation};
    }

    /* The :after pseudo-element creates a performant box shadow change on hover */
    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      box-shadow: ${levels.three};
      opacity: 0;
      transition: opacity 0.6s ${easeOutCirc};
      background: hsla(220, 30%, 40%, 0.17);
      mix-blend-mode: screen;
    }

    &:hover:after,
    &:focus:after {
      opacity: 1;
    }

    &:active:after {
      opacity: 0;
    }

    &:hover:focus:after {
      opacity: 0;
    }

    &:focus:not(:hover):after {
      transition: opacity 1.2s ${easeOutCirc};
    }
  }

  &:disabled {
    opacity: 0.54;
  }
`;

const detailsReveal = keyframes`
  from {
    opacity: 0;
    transform: translateX(9px);
  }
  to {
    opacity: 1;
    transform: translateX(0px);
  }
`;

const Detail = styled.div`
  position: absolute;
  right: 100%;
  top: 3px;
  margin-right: 9px;
  background: ${colors.white200};
  box-sizing: border-box;
  padding: 6px 12px;
  white-space: nowrap;
  box-shadow: ${levels.three};
  border-radius: 100px;

  animation: ${detailsReveal} 0.3s ease;
  transform: translateZ(0); /* Animation optimisation */
`;
